import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { loc, locArr } from "@/lib/localize";
import { GraduationCap, Briefcase, Users, Building2, Plane, FileCheck, Check } from "lucide-react";

const ICONS = {
  "graduation-cap": GraduationCap,
  "briefcase": Briefcase,
  "users": Users,
  "building-2": Building2,
  "plane": Plane,
  "file-check": FileCheck,
};

export default function ServicesPage() {
  const { t, lang } = useLang();
  const { content } = useContent();
  const [params] = useSearchParams();
  const q = (params.get("q") || "").toLowerCase();
  const service = params.get("service");
  const country = params.get("country");

  const services = useMemo(() => {
    let list = content?.services || [];
    if (service) list = list.filter((s) => s.id === service);
    if (q) {
      list = list.filter((s) => {
        const blob = (loc(s.title, lang) + " " + loc(s.short, lang) + " " + loc(s.description, lang)).toLowerCase();
        return blob.includes(q);
      });
    }
    return list;
  }, [content, service, q, lang]);

  return (
    <>
      <Header />
      <main className="bg-[var(--bg)]">
        <section className="pt-24 pb-12 bg-radial-gold border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="overline mb-3">{t("services.overline")}</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-tight mb-4">
              {t("services.title")}
            </h1>
            <p className="text-white/65 max-w-2xl mx-auto text-lg">{t("services.subtitle")}</p>
            {(service || q || country) && (
              <p className="mt-6 text-sm text-[var(--gold)]" data-testid="services-filter-info">
                Showing filtered results {q && `for "${q}"`} {service && `· ${service}`} {country && `· ${country.toUpperCase()}`}
              </p>
            )}
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
            {services.length === 0 && (
              <div className="text-center py-20" data-testid="no-services">
                <p className="text-white/60">No services match your criteria.</p>
              </div>
            )}
            {services.map((s, i) => {
              const Icon = ICONS[s.icon] || Briefcase;
              const flip = i % 2 === 1;
              const title = loc(s.title, lang);
              const description = loc(s.description, lang);
              const highlights = locArr(s.highlights, lang);
              return (
                <article
                  key={s.id}
                  id={s.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                    flip ? "lg:[direction:rtl]" : ""
                  }`}
                  data-testid={`service-detail-${s.id}`}
                >
                  <div className="relative rounded-2xl overflow-hidden gold-glow" style={{ direction: "ltr" }}>
                    <img src={s.image} alt={title} className="w-full h-[420px] object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-transparent" />
                    <div className="absolute top-5 left-5 w-12 h-12 grid place-items-center rounded-full bg-gradient-to-br from-[#ffb870] to-[#c45e00] text-black shadow-[0_6px_20px_-6px_rgba(244,133,30,0.7)]">
                      <Icon size={22} strokeWidth={2.2} />
                    </div>
                  </div>
                  <div style={{ direction: "ltr" }}>
                    <p className="overline mb-2">Service · {String(i + 1).padStart(2, "0")}</p>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">{title}</h2>
                    <p className="text-white/70 leading-relaxed mb-6">{description}</p>
                    <ul className="space-y-2.5">
                      {highlights.map((h) => (
                        <li key={h} className="flex items-start gap-2.5 text-white/85">
                          <Check size={18} className="text-[var(--gold)] mt-0.5 shrink-0" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
